import { ReactNode } from "react";
import TextGenerateEffect from "@/components/ui/TextGenerateEffect";
import MarkDown from "@/components/MarkDown";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils";

const variants = cva("relative z-30", {
  variants: {
    variant: {
      //h1: '~text-[2.125rem]/6xl ~leading-[2.25rem]/[4.25rem] font-black relative z-20 [&_em]:text-accent [&_em]:not-italic',
      h1: "text-balance font-sans font-black !leading-[0.935] ~text-4xl/7xl [&_em]:not-italic [&_em]:text-accent",
      h2: "text-balance font-sans font-extrabold ~text-3xl/5xl [&_em]:not-italic [&_em]:text-accent",
      h3: "text-balance font-sans font-extrabold ~text-2xl/4xl [&_em]:not-italic [&_em]:text-accent",
      h4: "text-balance font-sans font-bold ~text-xl/3xl [&_em]:not-italic [&_em]:text-accent",
      h5: "font-secondary font-bold uppercase tracking-wider ~text-sm/base",
      h6: "text-balance font-secondary text-xs uppercase tracking-widest text-primary/70 max-md:text-center max-md:text-primary/90",
    },
    invert: {
      true: "[&_.word_em]:text-mint [&_em]:text-white",
    },
  },
  defaultVariants: {
    variant: "h1",
  },
});

type Props = {
  children: ReactNode;
  fx?: boolean;
  level?: number;
  className?: string;
  invert?: boolean;
} & VariantProps<typeof variants>;

const Heading = ({
  children,
  fx = false,
  ...props
}: {
  children: string | ReactNode;
} & Props) => {
  const value = children ? children.toString() : "";
  return (
    <HeadingLevel {...props}>
      {fx ? (
        <TextGenerateEffect>{value}</TextGenerateEffect>
      ) : (
        <MarkDown skipParagraph>{value}</MarkDown>
      )}
    </HeadingLevel>
  );
};

const HeadingLevel = ({
  children,
  level,
  variant,
  className,
  invert,
  ...props
}: Props & {
  variant?: VariantProps<typeof variants>["variant"];
}) => {
  switch (level) {
    case 2:
      return (
        <h2
          className={cn(
            variants({
              variant: variant || "h2",
              invert,
              className,
            })
          )}
          {...props}>
          {children}
        </h2>
      );
    case 3:
      return (
        <h3
          className={cn(
            variants({
              variant: variant || "h3",
              invert,
              className,
            })
          )}
          {...props}>
          {children}
        </h3>
      );
    case 4:
      return (
        <h4
          className={cn(
            variants({
              variant: variant || "h4",
              invert,
              className,
            })
          )}
          {...props}>
          {children}
        </h4>
      );
    case 5:
      return (
        <h5
          className={cn(
            variants({
              variant: variant || "h5",
              invert,
              className,
            })
          )}
          {...props}>
          {children}
        </h5>
      );

    default:
      return (
        <h1
          className={cn(
            variants({
              variant: variant || "h1",
              invert,
              className,
            })
          )}
          {...props}>
          {children}
        </h1>
      );
  }
};

export default Heading;
